import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import rendezVousService, { IndisponibiliteVeterinaire } from '../../services/rendezvousService';

interface GestionIndisponibilitesProps {
  onIndisponibilitesChange?: () => void;
}

const GestionIndisponibilites: React.FC<GestionIndisponibilitesProps> = ({
  onIndisponibilitesChange
}) => {
  const [indisponibilites, setIndisponibilites] = useState<IndisponibiliteVeterinaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndispo, setEditingIndispo] = useState<IndisponibiliteVeterinaire | null>(null);
  const [formData, setFormData] = useState({
    date_debut: null as Date | null,
    date_fin: null as Date | null,
    type_indisponibilite: 'conges' as string,
    motif: ''
  });

  const typesIndispo = rendezVousService.getTypesIndisponibilite();

  useEffect(() => {
    loadIndisponibilites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIndisponibilites = async () => {
    setLoading(true);
    try {
      const data = await rendezVousService.getIndisponibilitesFutures();
      setIndisponibilites(data);
      setError(null);
      onIndisponibilitesChange?.();
    } catch (err) {
      setError('Erreur lors du chargement des indisponibilités');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date_debut || !formData.date_fin) {
      setError('Veuillez remplir les dates');
      return;
    }
    if (formData.date_fin <= formData.date_debut) {
      setError('La date de fin doit être postérieure à la date de début');
      return;
    }

    try {
      const data = {
        date_debut: formData.date_debut.toISOString(),
        date_fin: formData.date_fin.toISOString(),
        type_indisponibilite: formData.type_indisponibilite,
        motif: formData.motif || undefined
      };

      if (editingIndispo) {
        await rendezVousService.updateIndisponibilite(editingIndispo.id, data);
      } else {
        await rendezVousService.createIndisponibilite(data);
      }

      await loadIndisponibilites();
      handleCloseDialog();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error('Erreur:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette indisponibilité ?')) {
      return;
    }

    try {
      await rendezVousService.deleteIndisponibilite(id);
      await loadIndisponibilites();
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Erreur:', err);
    }
  };

  const handleEdit = (indispo: IndisponibiliteVeterinaire) => {
    setEditingIndispo(indispo);
    setFormData({
      date_debut: new Date(indispo.date_debut),
      date_fin: new Date(indispo.date_fin),
      type_indisponibilite: indispo.type_indisponibilite,
      motif: indispo.motif || ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingIndispo(null);
    setFormData({
      date_debut: null,
      date_fin: null,
      type_indisponibilite: 'conges',
      motif: ''
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeLabel = (type: string) => {
    return typesIndispo.find(t => t.value === type)?.label || type;
  };

  const getTypeColor = (type: string) => {
    return typesIndispo.find(t => t.value === type)?.color || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Périodes sans RDV
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Ajouter
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {indisponibilites.length === 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Box textAlign="center" py={3}>
                <EventBusyIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Aucune indisponibilité planifiée
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Ajoutez des congés, formations ou autres périodes où vous ne prenez pas de RDV
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={1}>
            {indisponibilites.map((indispo) => (
              <Card key={indispo.id} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Box>
                      <Typography variant="body1">
                        {formatDateTime(indispo.date_debut)} → {formatDateTime(indispo.date_fin)}
                      </Typography>
                      {indispo.motif && (
                        <Typography variant="body2" color="text.secondary">
                          {indispo.motif}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={getTypeLabel(indispo.type_indisponibilite)}
                        color={getTypeColor(indispo.type_indisponibilite) as any}
                        size="small"
                      />
                      <IconButton size="small" onClick={() => handleEdit(indispo)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(indispo.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingIndispo ? 'Modifier l\'indisponibilité' : 'Ajouter une indisponibilité'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <DateTimePicker
                label="Date et heure de début"
                value={formData.date_debut}
                onChange={(v) => setFormData(prev => ({ ...prev, date_debut: v ? new Date(v as Date) : null }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DateTimePicker
                label="Date et heure de fin"
                value={formData.date_fin}
                onChange={(v) => setFormData(prev => ({ ...prev, date_fin: v ? new Date(v as Date) : null }))}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type_indisponibilite}
                  label="Type"
                  onChange={(e) => setFormData(prev => ({ ...prev, type_indisponibilite: e.target.value }))}
                >
                  {typesIndispo.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Motif (optionnel)"
                value={formData.motif}
                onChange={(e) => setFormData(prev => ({ ...prev, motif: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingIndispo ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default GestionIndisponibilites;
